// src/pages/admin/Quotations.tsx
//
// Quotations desk — LIVE Shop backend (/api/quotations). Drafts move
// through the explicit server-side transition map
// (draft -> sent/expired, sent -> accepted/refused/expired; the rest are
// terminal). Creating and transitioning require the admin write role.

import { useCallback, useEffect, useState } from "react";
import { FileText, Pencil, Plus, RefreshCcw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { CustomerPicker, type CustomerSelection } from "@/components/opsdesk/CustomerPicker";
import { useAuth } from "@/components/auth/AuthContext";
import { cn } from "@/lib/cn";
import { formatPrice } from "@/lib/formatPrice";
import {
  createQuotation,
  fetchQuotations,
  removeQuotation,
  setQuotationStatus,
  updateQuotation,
  QUOTE_TRANSITIONS,
  type QuotationRow,
  type QuoteStatus,
} from "@/lib/opsdesk/api";

const STATUS_LABEL_KEY: Record<QuoteStatus, string> = {
  draft: "admin.quotations.statusDraft",
  sent: "admin.quotations.statusSent",
  accepted: "admin.quotations.statusAccepted",
  refused: "admin.quotations.statusRefused",
  expired: "admin.quotations.statusExpired",
};

const STATUS_VARIANT: Record<QuoteStatus, BadgeVariant> = {
  draft: "default",
  sent: "info",
  accepted: "success",
  refused: "danger",
  expired: "warning",
};

const STATUS_ORDER: QuoteStatus[] = [
  "draft",
  "sent",
  "accepted",
  "refused",
  "expired",
];

export function AdminQuotations() {
  const auth = useAuth();
  const { t } = useTranslation();
  // Removal is ADMIN ONLY. The server refuses anyone else; hiding the
  // button is a courtesy, not the control.
  const isAdmin = auth.accountType === "admin";
  const [quotes, setQuotes] = useState<QuotationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | "all">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchQuotations(statusFilter, showRemoved);
      setQuotes(body.rows);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showRemoved]);

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) void refresh();
  }, [auth.isReady, auth.isAuthenticated, refresh]);

  async function transition(quote: QuotationRow, to: QuoteStatus) {
    setBusyId(quote.quote_id);
    setError("");
    try {
      await setQuotationStatus(quote.quote_id, to);
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(quote: QuotationRow) {
    if (
      !window.confirm(
        t("admin.quotations.removeConfirm", { id: quote.quote_id }),
      )
    ) {
      return;
    }
    setBusyId(quote.quote_id);
    setError("");
    try {
      await removeQuotation(quote.quote_id);
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileText
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.quotations.title")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {quotes.length} {quotes.length === 1 ? t("admin.quotations.quotationOne") : t("admin.quotations.quotationOther")}
            {statusFilter !== "all"
              ? ` ${t("admin.quotations.withStatus")} ${t(STATUS_LABEL_KEY[statusFilter])}`
              : ""}
            {t("admin.quotations.statusNote")}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("admin.quotations.newQuotation")}
        </Button>
      </header>

      {showCreate && (
        <CreateQuotationForm
          onCreated={() => {
            setShowCreate(false);
            void refresh();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && (
        <div
          role="alert"
          className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose"
        >
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-fg-quaternary">{t("admin.quotations.statusLabel")}</span>
        {(["all", ...STATUS_ORDER] as Array<QuoteStatus | "all">).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            aria-pressed={statusFilter === s}
            className={cn(
              "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
              statusFilter === s
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
            )}
          >
            {s === "all" ? t("admin.quotations.all") : t(STATUS_LABEL_KEY[s])}
          </button>
        ))}
        {isAdmin && (
          <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-fg-quaternary">
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(e) => setShowRemoved(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent-rose"
            />
            {t("admin.quotations.showRemoved")}
          </label>
        )}
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-bg-canvas/30 p-3 text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-3.5 w-3.5 animate-spin" />
          {t("admin.quotations.loadingQuotations")}
        </div>
      ) : quotes.length === 0 ? (
        <EmptyState
          icon={
            <FileText
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("admin.quotations.emptyTitle")}
          description={t("admin.quotations.emptyDescription")}
        />
      ) : (
        <div className="space-y-2.5">
          {quotes.map((quote) =>
            editingId === quote.quote_id ? (
              <EditQuotationForm
                key={quote.quote_id}
                quote={quote}
                onSaved={() => {
                  setEditingId(null);
                  void refresh();
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
            <Card key={quote.quote_id}>
              <CardContent className="grid gap-3 p-4 sm:grid-cols-[1.2fr_2fr_auto] sm:items-center">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] text-fg-quaternary">
                    {quote.quote_id}
                  </div>
                  <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
                    {quote.customer_name}
                  </div>
                  <div className="text-[11px] text-fg-tertiary">
                    {quote.amount_htva !== null
                      ? `${formatPrice(Number(quote.amount_htva), "EUR")} HTVA`
                      : t("admin.quotations.amountTbd")}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_VARIANT[quote.status]} size="sm">
                      {t(STATUS_LABEL_KEY[quote.status])}
                    </Badge>
                    {quote.removed_at && (
                      <Badge variant="danger" size="sm">
                        {t("admin.quotations.removed")}
                      </Badge>
                    )}
                  </div>
                  <p className="line-clamp-2 text-[11px] text-fg-tertiary">
                    {quote.subject}
                  </p>
                  {quote.note && (
                    <p className="line-clamp-1 text-[10px] text-fg-quaternary">
                      {quote.note}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* Modify is ADMIN ONLY and drafts only: the server
                      enforces both; showing the control only there is a
                      courtesy, not the control. */}
                  {isAdmin && quote.status === "draft" && !quote.removed_at && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === quote.quote_id}
                      aria-label={t("admin.quotations.editAria", { id: quote.quote_id })}
                      onClick={() => setEditingId(quote.quote_id)}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("admin.quotations.edit")}
                    </Button>
                  )}
                  {QUOTE_TRANSITIONS[quote.status].length > 0 ? (
                    QUOTE_TRANSITIONS[quote.status].map((to) => (
                      <Button
                        key={to}
                        size="sm"
                        variant={to === "accepted" ? "primary" : "secondary"}
                        disabled={busyId === quote.quote_id}
                        onClick={() => void transition(quote, to)}
                      >
                        {t("admin.quotations.markAs")} {t(STATUS_LABEL_KEY[to]).toLowerCase()}
                      </Button>
                    ))
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                      {t("admin.quotations.terminal")}
                    </span>
                  )}
                  {isAdmin && !quote.removed_at && (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busyId === quote.quote_id}
                      aria-label={t("admin.quotations.removeAria", { id: quote.quote_id })}
                      onClick={() => void remove(quote)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("admin.quotations.remove")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function CreateQuotationForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerSelection | null>(null);
  const [subject, setSubject] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const amountTrimmed = amount.trim();
  const amountValid =
    amountTrimmed === "" || /^\d+(\.\d{1,2})?$/.test(amountTrimmed);
  const canSubmit =
    customer !== null && subject.trim().length > 0 && amountValid && !busy;

  async function submit() {
    if (!customer) return;
    setBusy(true);
    setError("");
    try {
      await createQuotation({
        customer_id: customer.customer_id,
        subject: subject.trim(),
        amount_htva: amountTrimmed === "" ? null : amountTrimmed,
        note: note.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
          {t("admin.quotations.newQuotationDraft")}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.quotations.customer")}
            </label>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.quotations.amountHtvaOptional")}
            </label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t("admin.quotations.amountPlaceholder")}
              inputMode="decimal"
            />
            {!amountValid && (
              <p className="text-[10px] text-accent-rose">
                {t("admin.quotations.amountError")}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.quotations.subject")}
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("admin.quotations.subjectPlaceholder")}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.quotations.noteOptional")}
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-xs text-fg-primary placeholder:text-fg-quaternary focus:border-benchmark-teal/60 focus:outline-none"
          />
        </div>
        {error && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("admin.quotations.cancel")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void submit()}>
            {t("admin.quotations.createDraft")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditQuotationForm({
  quote,
  onSaved,
  onCancel,
}: {
  quote: QuotationRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerSelection | null>({
    customer_id: quote.customer_id,
    name: quote.customer_name,
  });
  const [subject, setSubject] = useState(quote.subject);
  const [amount, setAmount] = useState(quote.amount_htva ?? "");
  const [note, setNote] = useState(quote.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const amountTrimmed = amount.trim();
  const amountValid =
    amountTrimmed === "" || /^\d+(\.\d{1,2})?$/.test(amountTrimmed);
  const canSubmit =
    customer !== null && subject.trim().length > 0 && amountValid && !busy;

  async function submit() {
    if (!customer) return;
    setBusy(true);
    setError("");
    try {
      await updateQuotation(quote.quote_id, {
        customer_id: customer.customer_id,
        subject: subject.trim(),
        amount_htva: amountTrimmed === "" ? null : amountTrimmed,
        note: note.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
          {t("admin.quotations.editQuotationDraft", { id: quote.quote_id })}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.quotations.customer")}
            </label>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.quotations.amountHtvaOptional")}
            </label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t("admin.quotations.amountPlaceholder")}
              inputMode="decimal"
            />
            {!amountValid && (
              <p className="text-[10px] text-accent-rose">
                {t("admin.quotations.amountError")}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.quotations.subject")}
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("admin.quotations.subjectPlaceholder")}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.quotations.noteOptional")}
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-xs text-fg-primary placeholder:text-fg-quaternary focus:border-benchmark-teal/60 focus:outline-none"
          />
        </div>
        {error && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("admin.quotations.cancel")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void submit()}>
            {t("admin.quotations.saveChanges")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
